import React, { useState, useEffect, useRef } from 'react';
import { personaleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileUp, Loader2, Users, CheckCircle2, AlertTriangle, Download, Trash2, FileSpreadsheet,
} from 'lucide-react';
import AcquistiHeader from '../../components/acquisti/AcquistiHeader';
import LinkGuida from '../../components/promemoria/LinkGuida';
import CostoPersonaleCard from '../../components/acquisti/CostoPersonaleCard';
import TrendPersonaleCard from '../../components/acquisti/TrendPersonaleCard';
import { fmtSoldi, fmtQta, ND } from '../../lib/acquisti';

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const CampoNumero = ({ label, valore, onChange, testid, step = '0.01' }) => (
  <div>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    <Input
      type="number"
      step={step}
      value={valore ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      className="h-10 tabular-nums"
      data-testid={testid}
    />
  </div>
);

const AcquistiPersonalePage = () => {
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const inputRef = useRef(null);
  const [dati, setDati] = useState(null);
  const [caricando, setCaricando] = useState(true);
  const [analisi, setAnalisi] = useState(null);
  const [bozza, setBozza] = useState(null);
  const [analizzando, setAnalizzando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [esportando, setEsportando] = useState(false);
  const [meseAperto, setMeseAperto] = useState(null);

  const carica = async () => {
    setCaricando(true);
    try {
      setDati(await personaleApi.lista());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setCaricando(false);
    }
  };
  useEffect(() => { carica(); }, []);

  const analizza = async (file) => {
    if (!file) return;
    setAnalizzando(true);
    try {
      const res = await personaleApi.analizza(file);
      setAnalisi(res);
      const t = res.anteprima.totali || {};
      const p = res.anteprima.periodo || {};
      setBozza({
        upload_id: res.upload_id,
        anno: p.anno || new Date().getFullYear(),
        mese: p.mese || 1,
        retribuzione: t.retribuzione ?? null,
        contribuzione: t.contribuzione ?? null,
        inail: t.inail ?? null,
        totale: t.totale ?? null,
        ore: t.ore ?? null,
        costo_medio_ora: t.costo_medio_ora ?? null,
        n_dipendenti: null,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF non riconosciuto');
    } finally {
      setAnalizzando(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const salva = async () => {
    setSalvando(true);
    try {
      const res = await personaleApi.conferma(bozza);
      toast.success(res.sostituito
        ? `${MESI[res.mese - 1]} ${res.anno} aggiornato: ${fmtSoldi(res.totale)}`
        : `${MESI[res.mese - 1]} ${res.anno} salvato: ${fmtSoldi(res.totale)}`);
      setAnalisi(null);
      setBozza(null);
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    } finally {
      setSalvando(false);
    }
  };

  const elimina = async (m) => {
    try {
      await personaleApi.elimina(m.id);
      toast.success(`${m.periodo_label} eliminato`);
      carica();
    } catch (e) {
      toast.error('Eliminazione non riuscita');
    }
  };

  const scaricaPdf = async (m) => {
    try {
      const blob = await personaleApi.pdf(m.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `costo-personale-${m.anno}-${String(m.mese).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      toast.error('PDF non disponibile');
    }
  };

  const esporta = async () => {
    setEsportando(true);
    try {
      const nome = await personaleApi.exportXlsx();
      toast.success(`Excel scaricato: ${nome}`);
    } catch (e) {
      toast.error('Export non riuscito');
    } finally {
      setEsportando(false);
    }
  };

  const ultimo = dati?.ultimo;
  const verifica = analisi?.anteprima?.verifica;
  const righeDettaglio = analisi?.anteprima?.dettaglio || [];
  const bustePagaBozza = righeDettaglio
    .filter((d) => d.tipo !== 'tfr')
    .reduce((s, d) => s + (d.retribuzione || 0), 0);

  return (
    <div className="min-h-screen pb-24">
      <AcquistiHeader titolo="Costo del personale" sottotitolo="Buste paga, contributi e personale extra" largo />

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4 lg:max-w-[1600px] lg:px-6 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0 lg:items-start 2xl:grid-cols-3">
        <div className="lg:col-span-2 2xl:col-span-3">
          <LinkGuida testo="Dove si scarica il report da Zucchetti?" />
        </div>
        {isAdmin && (
          <Card>
            <CardContent className="p-4 space-y-2">
              <input
                ref={inputRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => analizza(e.target.files?.[0])}
                data-testid="personale-file-input"
              />
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={() => inputRef.current?.click()}
                disabled={analizzando}
                data-testid="carica-report-btn"
              >
                {analizzando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
                Carica il report PDF del mese
              </Button>
              <p className="text-[11px] text-muted-foreground">
                Report "Statistica costo orario" di Paghe Infinity: leggo buste paga, contributi
                (INPS, INAIL, TFR) e ore lavorate e ti mostro i numeri da confermare prima di salvare.
              </p>
              <Button
                variant="secondary"
                className="w-full h-10"
                onClick={esporta}
                disabled={esportando || !dati?.n_mesi}
                data-testid="personale-export-xlsx-btn"
              >
                {esportando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                Esporta XLSX (dati esplosi)
              </Button>
              <p className="text-[11px] text-muted-foreground">
                Un foglio piatto con una riga per ogni importo (voce del cedolino, componente e uscite
                di cassa) e il contesto del mese: pronto per pivot e dashboard esterne.
              </p>
            </CardContent>
          </Card>
        )}

        {caricando && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground lg:col-span-2 2xl:col-span-3">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
          </div>
        )}

        {!caricando && !ultimo && (
          <Card data-testid="personale-vuoto">
            <CardContent className="p-4 text-sm text-muted-foreground">
              Nessun mese in archivio. Carica il report del libro paga per vedere costo del personale,
              incidenza sul venduto e costo per ora.
            </CardContent>
          </Card>
        )}

        {ultimo && (
          <Card data-testid="personale-ultimo-mese">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" /> {ultimo.periodo_label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CostoPersonaleCard mese={ultimo} />
            </CardContent>
          </Card>
        )}

        {dati?.n_mesi > 0 && <TrendPersonaleCard className="lg:col-span-2 2xl:col-span-3" />}

        {dati?.mesi?.length > 0 && (
          <Card data-testid="personale-lista" className="lg:col-span-2 2xl:col-span-3">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Mesi in archivio ({dati.n_mesi})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:space-y-0 2xl:grid-cols-3">
              {dati.mesi.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-2 py-2 border-b last:border-0" data-testid={`personale-mese-${m.anno}-${m.mese}`}>
                  <button
                    className="min-w-0 flex-1 text-left"
                    onClick={() => setMeseAperto(m)}
                    data-testid={`personale-apri-${m.anno}-${m.mese}`}
                  >
                    <p className="text-sm font-semibold">{m.periodo_label}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {fmtQta(m.ore)} ore · {m.kpi?.costo_ora_totale ? `${fmtSoldi(m.kpi.costo_ora_totale)}/ora` : 'ore n.d.'}
                      {m.kpi?.incidenza_totale_pct !== null ? ` · ${String(m.kpi.incidenza_totale_pct).replace('.', ',')}% sul venduto` : ''}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      buste paga {fmtSoldi(m.buste_paga)} · contributi {fmtSoldi(m.contributi)}
                      {m.personale_extra > 0 && (
                        <span className="text-primary" data-testid={`personale-extra-${m.anno}-${m.mese}`}>
                          {' '}· extra {fmtSoldi(m.personale_extra)}
                        </span>
                      )}
                    </p>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-sm font-bold tabular-nums" data-testid={`personale-totale-${m.anno}-${m.mese}`}>
                      {fmtSoldi(m.costo_totale)}
                    </span>
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => scaricaPdf(m)} data-testid={`pdf-${m.anno}-${m.mese}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => elimina(m)} data-testid={`elimina-${m.anno}-${m.mese}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              </div>
              <p className="text-[11px] text-muted-foreground pt-1">
                Tocca un mese per aprire la scheda con il dettaglio. Il costo del personale entra nel
                prospetto costi totali della Dashboard: non è un acquisto, resta contato a parte.
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      <Dialog open={!!bozza} onOpenChange={(o) => { if (!o) { setBozza(null); setAnalisi(null); } }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Confermi questi numeri?</DialogTitle>
            <DialogDescription>
              Li ho letti dal PDF: controlla e correggi se serve, poi salvo il mese.
            </DialogDescription>
          </DialogHeader>

          {bozza && (
            <div className="space-y-3" data-testid="personale-conferma">
              <div className="rounded-lg border p-3 text-xs text-muted-foreground space-y-1">
                <p>File: <strong className="text-foreground">{analisi?.anteprima?.sorgente?.nome_file}</strong></p>
                <p>Azienda: <strong className="text-foreground">{analisi?.anteprima?.azienda?.nome || ND}</strong></p>
                <p>Periodo letto: <strong className="text-foreground">{analisi?.anteprima?.periodo?.label || ND}</strong></p>
              </div>

              {verifica && (verifica.note?.length > 0 || !verifica.aritmetica_ok || !verifica.somma_dettaglio_ok) ? (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="personale-avvisi">
                  <p className="font-semibold flex items-center gap-1 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> Da controllare</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {verifica.note.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
              ) : (
                <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid="personale-verifica-ok">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Controlli superati: retribuzione + contributi + INAIL = totale
                </p>
              )}

              {analisi?.esistente && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="personale-esistente">
                  Per questo mese c'è già un valore in archivio ({fmtSoldi(analisi.esistente.totale)}):
                  salvando lo sostituisci.
                </div>
              )}

              {bustePagaBozza > 0 && (
                <div className="rounded-lg border p-3 text-xs space-y-0.5" data-testid="conferma-voci">
                  <p>Buste paga <strong>{fmtSoldi(bustePagaBozza)}</strong></p>
                  <p>Contributi (INPS, INAIL, TFR) <strong>{fmtSoldi((Number(bozza.totale) || 0) - bustePagaBozza)}</strong></p>
                  <p className="text-muted-foreground text-[11px]">
                    Sotto trovi le colonne così come sono scritte nel PDF: correggile solo se il
                    report riporta valori diversi.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <CampoNumero label="Anno" valore={bozza.anno} step="1" onChange={(v) => setBozza({ ...bozza, anno: v })} testid="campo-anno" />
                <CampoNumero label="Mese (1-12)" valore={bozza.mese} step="1" onChange={(v) => setBozza({ ...bozza, mese: v })} testid="campo-mese" />
                <CampoNumero label="Retribuzioni €" valore={bozza.retribuzione} onChange={(v) => setBozza({ ...bozza, retribuzione: v })} testid="campo-retribuzione" />
                <CampoNumero label="Contributi €" valore={bozza.contribuzione} onChange={(v) => setBozza({ ...bozza, contribuzione: v })} testid="campo-contribuzione" />
                <CampoNumero label="INAIL €" valore={bozza.inail} onChange={(v) => setBozza({ ...bozza, inail: v })} testid="campo-inail" />
                <CampoNumero label="Ore lavorate" valore={bozza.ore} onChange={(v) => setBozza({ ...bozza, ore: v })} testid="campo-ore" />
                <CampoNumero label="Costo azienda totale €" valore={bozza.totale} onChange={(v) => setBozza({ ...bozza, totale: v })} testid="campo-totale" />
                <CampoNumero label="N. dipendenti (opzionale)" valore={bozza.n_dipendenti} step="1" onChange={(v) => setBozza({ ...bozza, n_dipendenti: v })} testid="campo-dipendenti" />
              </div>

              {analisi?.anteprima?.dettaglio?.length > 0 && (
                <div className="border rounded-lg divide-y">
                  {analisi.anteprima.dettaglio.map((d, i) => (
                    <div key={i} className="p-2 text-xs flex items-center justify-between gap-2">
                      <span>{d.descrizione}</span>
                      <span className="font-semibold tabular-nums">{fmtSoldi(d.totale)}</span>
                    </div>
                  ))}
                </div>
              )}

              <Badge variant="outline" className="text-[10px]">
                Il PDF originale viene conservato insieme al dato
              </Badge>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setBozza(null); setAnalisi(null); }} data-testid="annulla-conferma-btn">Annulla</Button>
            <Button onClick={salva} disabled={salvando || !bozza?.totale} data-testid="salva-personale-btn">
              {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Salva il mese
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!meseAperto} onOpenChange={(v) => !v && setMeseAperto(null)}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="personale-mese-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" /> {meseAperto?.periodo_label}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Quanto è uscito per il personale in questo mese
            </DialogDescription>
          </DialogHeader>
          {meseAperto && <CostoPersonaleCard mese={meseAperto} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMeseAperto(null)} data-testid="chiudi-mese-dialog">
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AcquistiPersonalePage;
